<template>
    <label
        class="toggle-switch"
        :class="toggleClasses"
        :title="tooltip"
    >
        <input
            :id="toggleId"
            ref="input"
            v-model="internalValue"
            type="checkbox"
            :disabled="disabled"
            :name="name"
            class="toggle-input"
            @change="handleChange"
        />

        <div class="toggle-track">
            <i
                :class="[iconOff, 'toggle-icon', 'toggle-icon-off']"
                :style="{ color: offIconColor }"
            ></i>
            <i
                :class="[iconOn, 'toggle-icon', 'toggle-icon-on']"
                :style="{ color: onIconColor }"
            ></i>
            <div class="toggle-thumb"></div>
        </div>
    </label>
</template>

<script>
export default {
    name: 'ToggleSwitch',

    props: {
        // Valeur du toggle
        modelValue: {
            type: Boolean,
            default: false
        },

        // ID unique pour l'input
        id: {
            type: String,
            default: null
        },

        // Nom du champ
        name: {
            type: String,
            default: ''
        },

        // Variante de couleur
        variant: {
            type: String,
            default: 'success',
            validator(value) {
                return ['success', 'primary', 'secondary', 'error', 'warning'].includes(value)
            }
        },

        // Icône pour l'état OFF
        iconOff: {
            type: String,
            default: 'fas fa-times'
        },

        // Icône pour l'état ON
        iconOn: {
            type: String,
            default: 'fas fa-check'
        },

        // Couleur personnalisée pour l'icône OFF
        offIconColor: {
            type: String,
            default: '#6b7280'
        },

        // Couleur personnalisée pour l'icône ON
        onIconColor: {
            type: String,
            default: '#ffffff'
        },
        size: {
            type: String,
            default: 'md',
            validator(value) {
                return ['sm', 'md', 'lg'].includes(value)
            }
        },
        disabled: {
            type: Boolean,
            default: false
        },
        tooltip: {
            type: String,
            default: ''
        }
    },

    emits: ['update:modelValue', 'change'],

    data() {
        return {
            uniqueId: null
        }
    },

    computed: {
        internalValue: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('update:modelValue', value)
            }
        },

        toggleId() {
            return this.id || this.uniqueId || `toggle-${this._uid || Date.now()}`
        },

        toggleClasses() {
            return [
                `toggle-${this.variant}`,
                `toggle-${this.size}`,
                {
                    'toggle-disabled': this.disabled
                }
            ]
        }
    },

    created() {
        // Générer un ID unique si pas fourni
        if (!this.id) {
            this.uniqueId = `toggle-${Math.random().toString(36).substr(2, 9)}`
        }
    },

    methods: {
        handleChange(event) {
            const value = event.target.checked
            this.$emit('change', value)
            this.$emit('update:modelValue', value)
        },
        toggle() {
            if (!this.disabled) {
                this.internalValue = !this.internalValue
            }
        }
    }
}
</script>

<style scoped>
.toggle-switch {
    position: relative;
    display: inline-block;
    cursor: pointer;
    user-select: none;
}

.toggle-switch.toggle-disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

/* Input caché */
.toggle-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    z-index: -1;
}

.toggle-track {
    position: relative;
    width: 3rem;
    height: 1.5rem;
    background-color: var(--color-base-200);
    border-radius: .2rem;
    transition: all 0.2s ease-in-out;
    border: 2px solid var(--color-base-300);
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-track:hover {
    border-color: var(--color-base-400);
}

.toggle-disabled .toggle-track:hover {
    border-color: var(--color-base-300);
}

.toggle-thumb {
    position: absolute;
    top: .1rem;
    left: 0.125rem;
    width: 1rem;
    height: 1rem;
    background-color: white;
    border-radius: 4px;
    transition: all 0.2s ease-in-out;
    z-index: 10;
}

.toggle-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.8rem;
    transition: all 0.2s ease-in-out;
    z-index: 5;
    pointer-events: none;
}

.toggle-icon-off {
    right: 0.375rem;
    opacity: 1;
}

.toggle-icon-on {
    left: 0.375rem;
    opacity: 0;
}

.toggle-input:checked + .toggle-track .toggle-thumb {
    transform: translateX(1.375rem);
}

.toggle-input:checked + .toggle-track .toggle-icon-off {
    opacity: 0;
}

.toggle-input:checked + .toggle-track .toggle-icon-on {
    opacity: 1;
}

.toggle-success .toggle-input:checked + .toggle-track {
    background-color: var(--color-success);
    border-color: var(--color-success);
}

.toggle-success .toggle-track:hover {
    border-color: var(--color-success);
}

.toggle-primary .toggle-input:checked + .toggle-track {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
}

.toggle-primary .toggle-track:hover {
    border-color: var(--color-primary);
}

.toggle-secondary .toggle-input:checked + .toggle-track {
    background-color: var(--color-secondary);
    border-color: var(--color-secondary);
}

.toggle-secondary .toggle-track:hover {
    border-color: var(--color-secondary);
}

.toggle-error .toggle-input:checked + .toggle-track {
    background-color: var(--color-error);
    border-color: var(--color-error);
}

.toggle-error .toggle-track:hover {
    border-color: var(--color-error);
}

.toggle-warning .toggle-input:checked + .toggle-track {
    background-color: var(--color-warning);
    border-color: var(--color-warning);
}

.toggle-warning .toggle-track:hover {
    border-color: var(--color-warning);
}

/* Tailles */
.toggle-sm .toggle-track {
    width: 2.25rem;
    height: 1.125rem;
    border-radius: 1.125rem;
}

.toggle-sm .toggle-thumb {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 0.375rem;
    top: 1px;
}

.toggle-sm .toggle-icon {
    font-size: 0.5rem;
}

.toggle-sm .toggle-input:checked + .toggle-track .toggle-thumb {
    transform: translateX(1.125rem);
}

.toggle-lg .toggle-track {
    width: 4rem;
    height: 2rem;
    border-radius: 2rem;
}

.toggle-lg .toggle-thumb {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.75rem;
}

.toggle-lg .toggle-icon {
    font-size: 0.75rem;
}

.toggle-lg .toggle-input:checked + .toggle-track .toggle-thumb {
    transform: translateX(1.875rem);
}

/* Focus states */
.toggle-input:focus + .toggle-track {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
}

.toggle-input:focus:not(:focus-visible) + .toggle-track {
    outline: none;
}

/* Active state */
.toggle-switch:active .toggle-thumb {
    transform: scale(0.95);
}

.toggle-switch:active .toggle-input:checked + .toggle-track .toggle-thumb {
    transform: translateX(1.375rem) scale(0.95);
}

.toggle-sm.toggle-switch:active .toggle-input:checked + .toggle-track .toggle-thumb {
    transform: translateX(1.125rem) scale(0.95);
}

.toggle-lg.toggle-switch:active .toggle-input:checked + .toggle-track .toggle-thumb {
    transform: translateX(1.875rem) scale(0.95);
}
</style>
